import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import type { NewDeliverableInput } from '../../contexts/PortalContext';
import { DELIVERABLE_STAGES, DELIVERABLE_STATUSES, DELIVERABLE_TYPES } from '../../types/portal';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
import { TranscriptUpload } from './TranscriptUpload';
const EMPTY: NewDeliverableInput = {
  name: '',
  type: 'Video',
  quantity: '1',
  stage: 'Pre-Production',
  status: 'Not Started',
  version: '',
  driveUrl: '',
  audioUrl: '',
  internalNote: '',
  visibleToClient: false,
  transcript: ''
};
export function AddDeliverable() {
  const {
    addDeliverable,
    notify
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState<NewDeliverableInput>(EMPTY);
  function edit(patch: Partial<NewDeliverableInput>) {
    setInput(current => ({
      ...current,
      ...patch
    }));
  }
  function submit(event: React.FormEvent) {
    event.preventDefault();
    const name = input.name.trim();
    if (!name) return;
    /** Versions only exist once work reaches Review or Final Delivery. */
    const versioned = input.stage === 'Review' || input.stage === 'Final Delivery';
    addDeliverable({
      ...input,
      name,
      version: versioned ? input.version : ''
    });
    notify(`${name} added`);
    setInput(EMPTY);
    setOpen(false);
  }
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-8 inline-flex items-center gap-3`} data-id="element-136" data-mp="17nll5j8oueih">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-137" data-mp="1heq22ohxc9fy" />
        Add Deliverable
      </button>;
  }
  return <form onSubmit={submit} className="mt-8 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-138" data-mp="1chgdtc69u3tp">
      <h3 className="font-display text-2xl leading-none" data-id="element-139" data-mp="1qh7k0qqjqfx5">New Deliverable</h3>

      <label className="mt-8 block" data-id="element-140" data-mp="1li4j9ihlmvuz">
        <span className="label block text-paper/40" data-id="element-141" data-mp="1lj63acglesou">Name</span>
        <input value={input.name} onChange={event => edit({
        name: event.target.value
      })} placeholder="e.g. Speaker Interview 01" autoFocus className={FIELD} data-id="element-142" data-mp="j4zvgfiyiqmn" />
      </label>

      <div className="mt-8 grid grid-cols-2 gap-8 sm:grid-cols-4" data-id="element-143" data-mp="s3bvt99ml2sa">
        <label className="block" data-id="element-144" data-mp="wwdbhjhnp6cx">
          <span className="label block text-paper/40" data-id="element-145" data-mp="wxg2rsiw5atd">Type</span>
          <select value={input.type} onChange={event => edit({
          type: event.target.value as NewDeliverableInput['type']
        })} className={SELECT} data-id="element-146" data-mp="11td9d74cr2sy">
            {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-147" data-mp="1bqrn2yyw5dhk">
                {option}
              </option>)}
          </select>
        </label>
        <label className="block" data-id="element-148" data-mp="3dladyue6y754">
          <span className="label block text-paper/40" data-id="element-149" data-mp="3dmbrd5ioj5q0">Quantity</span>
          <input value={input.quantity} onChange={event => edit({
          quantity: event.target.value
        })} placeholder="e.g. 8" className={FIELD} data-id="element-150" data-mp="3hm2pjuajco4k" />
        </label>
        <label className="block" data-id="element-151" data-mp="35a08p5f6f2zq">
          <span className="label block text-paper/40" data-id="element-152" data-mp="30bysqtd1rjsh">Stage</span>
          <select value={input.stage} onChange={event => edit({
          stage: event.target.value as NewDeliverableInput['stage']
        })} className={SELECT} data-id="element-153" data-mp="30a5m3e2fozkh">
            {DELIVERABLE_STAGES.map(option => <option key={option} value={option} className={OPTION} data-id="element-154" data-mp="3o1krjfpibwzy">
                {option}
              </option>)}
          </select>
        </label>
        {input.stage === 'Review' || input.stage === 'Final Delivery' ? <label className="block" data-id="element-155" data-mp="3f0uzmn3daqri">
            <span className="label block text-paper/40" data-id="element-156" data-mp="28kspi42155yz">Version</span>
            <input value={input.version} onChange={event => edit({
          version: event.target.value
        })} placeholder="e.g. V1" className={FIELD} data-id="element-157" data-mp="28izfqfw4e5mr" />
          </label> : <div className="block" data-id="element-158" data-mp="4jaohr6xx9w">
            <span className="label block text-paper/40" data-id="element-159" data-mp="5kkyk0dufqk">Version</span>
            <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper/30" data-id="element-160" data-mp="3iis703f3mvl5">
              Added at Review
            </p>
          </div>}
      </div>

      <label className="mt-8 block" data-id="element-161" data-mp="e398jzlu40zb">
        <span className="label block text-paper/40" data-id="element-162" data-mp="e4agvwbgu9dc">Status</span>
        <select value={input.status} onChange={event => edit({
        status: event.target.value as NewDeliverableInput['status']
      })} className={SELECT} data-id="element-163" data-mp="j33xhxm1ughq">
          {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-164" data-mp="a0cjbq1sh6me">
              {option}
            </option>)}
        </select>
      </label>

      {input.type === 'Transcript' ? <TranscriptUpload value={input.transcript ?? ''} onChange={text => edit({
      transcript: text
    })} data-id="element-165" data-mp="2utzfqk9f9bwv" /> : <label className="mt-8 block" data-id="element-166" data-mp="2ia2hoc05t8gr">
          <span className="label block text-paper/40" data-id="element-167" data-mp="2i90w3dlip3ks">Google Drive Link</span>
          <input value={input.driveUrl} onChange={event => edit({
        driveUrl: event.target.value
      })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-168" data-mp="2w9ttq7748es4" />
        </label>}

      <label className="mt-8 block" data-id="element-169" data-mp="23ito5rt4z2yc">
        <span className="label block text-paper/40" data-id="element-170" data-mp="1ymbk6u5mr8n2">Notes</span>
        <input value={input.internalNote} onChange={event => edit({
        internalNote: event.target.value
      })} placeholder="Optional internal note" className={FIELD} data-id="element-171" data-mp="1yjqjw9kzd0rf" />
      </label>

      <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-172" data-mp="1ku56depy9dqs">
        <span className="label text-paper/40" data-id="element-173" data-mp="1fvlktff9q3yy">Visible to Client</span>
        <button type="button" onClick={() => edit({
        visibleToClient: !input.visibleToClient
      })} aria-pressed={input.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', input.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-174" data-mp="1fthgiwdgj2y5">
          {input.visibleToClient ? 'On' : 'Off'}
        </button>
      </label>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-175" data-mp="31xa8hwx2xsjn">
        <button type="submit" className={BUTTON_SOLID} data-id="element-176" data-mp="31zwj65f9oo63">
          Add Deliverable
        </button>
        <button type="button" onClick={() => {
        setInput(EMPTY);
        setOpen(false);
      }} className={BUTTON_QUIET} data-id="element-177" data-mp="361hxlv7g9aie">
          Cancel
        </button>
      </div>
    </form>;
}